/**
 * Layer 3：记忆偏好数据接入
 * @see https://tapd.tencent.com/tapd_fe/20427644/story/detail/1020427644135375711
 */
import { useCallback, useMemo } from 'react';
import { useIntl } from 'umi';
import { v4 as uuidv4 } from 'uuid';
import { useRequestController } from '../../../../../Home/hooks/useRequestController';
import type { IGetUserMemoriesResponse } from '../types';

const DEFAULT_SYSTEM_LANGUAGE = 'en-US';

export interface IUseMemoryPreferencesReturn {
  data: IGetUserMemoriesResponse | undefined;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  setUseMemory: (useMemory: boolean) => Promise<void>;
}

export const useMemoryPreferences = (): IUseMemoryPreferencesReturn => {
  const { locale } = useIntl();
  const systemLanguage = locale || DEFAULT_SYSTEM_LANGUAGE;

  const { data, loading, error, refresh, runAsync } = useRequestController({
    method: 'POST',
    url: '/api/agent_com/get_user_memories',
    refreshDeps: useMemo(
      () => ({
        trace_id: uuidv4(),
        system_language: systemLanguage,
      }),
      [systemLanguage],
    ),
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    cacheKey: `memory-preferences-get-${systemLanguage}`,
    loadingDelay: 300,
  });

  const setUseMemory = useCallback(
    async (useMemory: boolean) => {
      try {
        await runAsync({
          trace_id: uuidv4(),
          use_memory: useMemory,
          system_language: systemLanguage,
        });
      } catch (err) {
        console.error('设置记忆偏好失败:', err);
        throw err;
      }
    },
    [runAsync, systemLanguage],
  );

  return {
    data,
    loading,
    error,
    refresh,
    setUseMemory,
  };
};
